import {
  emptySiteCart,
  type SiteCartSnapshot,
  type SiteCatalogProduct,
} from '~/lib/site-catalog'

export const SITE_CART_KEY = Symbol('site-cart')

/** Плоская копия без прокси $state (structuredClone с ними падает) */
function cloneCartSnapshot(snapshot: SiteCartSnapshot): SiteCartSnapshot {
  return {
    count: snapshot.count,
    total: snapshot.total,
    items: snapshot.items.map((item) => ({
      id: item.id,
      productId: item.productId,
      quantity: item.quantity,
      lineTotal: item.lineTotal,
      product: {
        id: item.product.id,
        name: item.product.name,
        slug: item.product.slug,
        price: item.product.price,
        imageUrl: item.product.imageUrl,
        stockQuantity: item.product.stockQuantity,
      },
    })),
  }
}

/** Локальная корзина с оптимистичным обновлением (мгновенный UI) */
export class SiteCartStore {
  snapshot = $state<SiteCartSnapshot>(emptySiteCart())

  hydrate(server: SiteCartSnapshot | undefined): void {
    this.snapshot = server ? cloneCartSnapshot(server) : emptySiteCart()
  }

  lineQty(productId: number): number {
    return this.snapshot.items.find((i) => i.productId === productId)?.quantity ?? 0
  }

  /** Мгновенное обновление до ответа сервера */
  applyOptimistic(product: SiteCatalogProduct, quantity: number): void {
    const items = [...this.snapshot.items]
    const idx = items.findIndex((i) => i.productId === product.id)

    if (quantity <= 0) {
      if (idx >= 0) {
        items.splice(idx, 1)
      }
    } else if (idx >= 0) {
      const line = items[idx]
      items[idx] = {
        ...line,
        quantity,
        lineTotal: product.price * quantity,
        product: {
          ...line.product,
          stockQuantity: product.quantity,
        },
      }
    } else {
      items.push({
        id: -product.id,
        productId: product.id,
        quantity,
        lineTotal: product.price * quantity,
        product: {
          id: product.id,
          name: product.name,
          slug: product.slug,
          price: product.price,
          imageUrl: product.imageUrl,
          stockQuantity: product.quantity,
        },
      })
    }

    const count = items.reduce((sum, item) => sum + item.quantity, 0)
    const total = items.reduce((sum, item) => sum + item.lineTotal, 0)
    this.snapshot = { count, total, items }
  }

  applyServer(server: SiteCartSnapshot): void {
    this.snapshot = cloneCartSnapshot(server)
  }

  rollback(previous: SiteCartSnapshot): void {
    this.snapshot = cloneCartSnapshot(previous)
  }

  cloneSnapshot(): SiteCartSnapshot {
    return cloneCartSnapshot(this.snapshot)
  }
}

/** Одна корзина на всё приложение (не пересоздавать в SiteLayout) */
export const siteCartStore = new SiteCartStore()

/**
 * Подстановка с сервера без затирания оптимистичных изменений
 * (props.siteCart после POST ещё может быть пустым).
 */
export function syncSiteCartFromServer(server: SiteCartSnapshot | undefined | null): void {
  const next = server ?? emptySiteCart()
  const local = siteCartStore.snapshot

  // props.siteCart после POST может отставать — не затираем оптимистичную корзину
  if (next.count === 0 && local.count > 0) {
    return
  }

  // После F5 store пустой — сразу подставляем сервер
  if (local.count === 0 && next.count > 0) {
    siteCartStore.hydrate(next)
    return
  }

  if (
    next.count !== local.count ||
    next.total !== local.total ||
    next.items.length !== local.items.length
  ) {
    siteCartStore.hydrate(next)
  }
}
